// ── Core Beatmap Types (mirrors backend models) ─────────────────────────────

import type { DesignShape } from './designShape';

export interface Note {
  time_ms: number;
  time_seconds: number;
  time_beats: number;
  position: [number, number, number];
  note_type: string;
  hand: 'right' | 'left' | 'special';
  direction: number;
  has_rail: boolean;
  rail_nodes: [number, number, number][] | null;
  combo_id: number;
  note_id: string;
  /** Synth Console: this note links into a chain/combo (renders the chain line).
   *  Absent/false on VR maps. */
  is_chain_note?: boolean;
}

export interface Slide {
  time_ms: number;
  time_seconds: number;
  time_beats: number;
  slide_type: number;
  wall_type: string;
  initialized: boolean;
  /** Per-instance world position [x, y, z] when the wall has been moved off its
   *  wall_type default. Round-trips through the .synth "position" field. */
  position?: number[] | null;
  /** Synth Console: obstacle roll/rotation in degrees (Z axis). Absent on VR. */
  z_rotation?: number;
}

// ── Console obstacle (crouch / square / triangle) ───────────────────────────
// Synth Console stores these as full objects with position + rotation (unlike
// the legacy VR flat-time crouch list). Mirrors backend ObstacleOut.
export interface Obstacle {
  time_ms: number;
  time_seconds: number;
  time_beats: number;
  /** World position [x, y, z]; null = use the type's default lane. */
  position?: number[] | null;
  /** Roll/rotation in degrees (Z axis). */
  z_rotation: number;
  initialized: boolean;
}

export interface Difficulty {
  name: string;
  note_count: number;
  rail_count: number;
  slide_count: number;
  crouch_count: number;
  jump_count: number;
  duration_seconds: number;
  duration_beats: number;
  notes: Note[];
  slides: Slide[];
  crouches: number[];     // time_ms values (flat list — backward compat / VR)
  jumps: number[];         // time_ms values
  // ── Synth Console: full per-obstacle objects (position + z_rotation) ──
  // Backend serves these for console maps; absent on VR maps.
  crouch_obstacles?: Obstacle[];
  squares?: Obstacle[];
  triangles?: Obstacle[];
}

// ── Flash Light Event (global, not per-difficulty) ──────────────────────────

export interface FlashEvent {
  time_beats: number;          // beat position (same frame as note.time_beats)
  color: string;               // hex color, e.g. '#FBBF24' (in-game the flash is white)
  intensity: number;           // 0.0 - 5.0 (light multiplier)
  fadeInTime: number;          // seconds for the flash to reach full intensity
  fadeOutTime: number;         // seconds for the flash to fade to zero
  duration?: number;           // seconds each flash pulse stays on (newer model; replaces fadeIn+Out)
  repeat?: number;             // number of on/off pulses — >1 makes a strobe (default 1)
  frequency?: number;          // strobe rate in Hz: pulses/second (interval = 1/frequency)
}

// ── Named Bookmark (global, not per-difficulty) ─────────────────────────────

export interface BookmarkEntry {
  beat: number;                // beat position
  name: string;                // user-assigned label
  color?: number;              // 0xRRGGBB from the bookmark palette; absent or 0 = default look
}

export interface Beatmap {
  id: string;
  name: string;
  author: string;
  beatmapper: string;
  bpm: number;
  offset: number;
  audio_name: string;
  has_audio: boolean;
  has_artwork: boolean;
  difficulties: Record<string, Difficulty>;
  flashEvents: FlashEvent[];
  /** Flash effects: the beats of each difficulty's `Effects` list (per difficulty, unlike lights). */
  effects?: Record<string, number[]>;
  /** Draft Mode (the inverse of the game's `ProductionMode`): a draft map records no player scores. Absent means on. */
  draft_mode?: boolean;
  /** The sixth, "Custom" difficulty's display name and speed (the game's `CustomDifficultyName` / `CustomDifficultySpeed`). Absent means "Custom" and 1. */
  custom_difficulty_name?: string;
  custom_difficulty_speed?: number;
  bookmarks: BookmarkEntry[];
  /** Full Shape Designer decor groups embedded in the .synth (Synapse-only), so
   *  re-importing a map restores the composite wall art. */
  decorWalls?: DesignShape[];
}

export interface BeatmapSummary {
  id: string;
  filename: string;
  uploaded_at: number;
  name: string;
  author: string;
  beatmapper: string;
  bpm: number;
  has_audio: boolean;
  /** Absolute path of the .synth file on the user's PC — present only for maps
   *  loaded via a folder scan (null for browser uploads). Enables the row's
   *  "open folder" button. */
  source_path?: string | null;
  difficulties: Record<string, { notes: number; rails: number; slides: number }>;
  owner: string;
  visibility: 'private' | 'public';
  is_owner: boolean;
}

export interface UploadResult {
  success: boolean;
  message: string;
  beatmap: Beatmap | null;
  error: string | null;
  /**
   * Whether the map reached durable storage, as opposed to only this server's
   * memory. A map can parse, open and edit perfectly with `persisted: false`
   * and still be gone after the next deploy, so this is not the same question
   * as `success` and the two must not be collapsed.
   */
  persisted?: boolean;
}

export interface BatchUploadResult {
  total: number;
  successful: number;
  failed: number;
  results: UploadResult[];
}

// ── Editor Types ────────────────────────────────────────────────────────────

export type ToolMode =
  | 'select' | 'eraser'
  // Note placement
  | 'note_right' | 'note_left' | 'note_onehand' | 'note_bothhands'
  // Rail/Line placement
  | 'rail_right' | 'rail_left' | 'rail_onehand' | 'rail_bothhands'
  // Legacy aliases (kept for backward compat)
  | 'rail' | 'wall' | 'crouch' | 'jump'
  // Obstacle subtypes
  | 'wall_left' | 'wall_right' | 'wall_diag_left' | 'wall_diag_right' | 'wall_center'
  // Square and Triangle walls — stored in their own `squares` / `triangles` collections, not as slides
  | 'wall_square' | 'wall_triangle'
  // Special events
  | 'flash_effect' | 'bookmark';

export type SnapDivision = 4 | 8 | 16 | 32 | 64;

export type CameraView = 'default' | 'left' | 'right' | 'free' | 'custom' | 'cam1' | 'cam2' | 'cam3';

/** Synth VR's three user cameras. Each behaves like the free camera but keeps its own pose and name. */
export const VR_CAM_VIEWS = ['cam1', 'cam2', 'cam3'] as const;
export type VrCamView = typeof VR_CAM_VIEWS[number];
export const isVrCamView = (v: string | undefined): v is VrCamView => v === 'cam1' || v === 'cam2' || v === 'cam3';
/** Views driven by the mouse (Unity-style fly controls) instead of a fixed pose. */
export const isFreeLookView = (v: string | undefined): boolean => v === 'free' || isVrCamView(v);

export interface VrCamSlot {
  name: string;
  position: [number, number, number] | null;
  rotation: [number, number, number] | null;
}
export const DEFAULT_VR_CAM_NAMES = ['Custom 1', 'Custom 2', 'Custom 3'] as const;

export type PlaybackSpeed = 0.25 | 0.5 | 1 | 2;

export interface PlaybackState {
  playing: boolean;
  currentTime: number;   // seconds
  currentBeat: number;
  duration: number;       // seconds
}

export interface CursorPosition {
  beat: number;
  x: number;
  y: number;
}

// ── Constants ───────────────────────────────────────────────────────────────

export const GRID_SCALE = 0.1365;
export const TIME_SCALE = 20;
export const INDEX_SCALE = 64;

/** Default note colors — prefer getNoteColorsFromSettings(gameplaySettings) from the store. */
export const NOTE_COLORS: Record<string, string> = {
  right_hand: '#FF69B4',       // Pink
  left_hand: '#4488FF',        // Blue
  one_hand_special: '#44DD66', // Green
  both_hands_special: '#FFD700', // Yellow
};

export const WALL_COLORS: Record<string, string> = {
  wall_right: '#ff444488',
  wall_left: '#4444ff88',
  angle_right: '#ff884488',
  center: '#ffff4488',
  angle_left: '#4488ff88',
  crouch: '#88888888',
  jump: '#44ff4488',
};

/**
 * Parse 8-digit hex color (#RRGGBBAA) into separate color + alpha.
 * Three.js Color doesn't handle alpha in hex strings.
 */
export function parseWallColor(hex8: string): { color: string; alpha: number } {
  const rgb = hex8.slice(0, 7);
  const a = hex8.length === 9 ? parseInt(hex8.slice(7, 9), 16) / 255 : 1;
  return { color: rgb, alpha: a };
}

export const DIFFICULTIES = ['Easy', 'Normal', 'Hard', 'Expert', 'Master', 'Custom'] as const;

/** The canonical default difficulty set offered in every editor. Maps may also
 *  contain any number of additional custom-named difficulties (free strings). */
export const DEFAULT_DIFFICULTIES = ['Easy', 'Normal', 'Hard', 'Expert', 'Master'] as const;
